/**
 * "Promover avaliação" modal — the backward-compatible on-ramp: turn an existing
 * one-shot evaluation into a tracked candidate's Round 1, preserving the score
 * (no re-run). There is no "list my recent evaluations" RPC, so the operator
 * pastes the evaluation's share link (or id) — the same id that addresses the
 * `/e/:evaluationId` report they came from — and we fetch a preview card before
 * confirming, reproducing the design's promo-card with real data.
 */

import type { LevelAssessmentResult } from "@shared/labs/level-assessment";
import { ArrowUpFromLine } from "lucide-react";
import { useEffect, useState } from "react";
import { rpcClient } from "@/lib/rpc";
import {
  Avatar,
  C,
  ConfidenceChip,
  DM,
  ErrorText,
  Field,
  GhostButton,
  LevelPill,
  ModalShell,
  OverlayActions,
  OverlayHeader,
  PrimaryButton,
  TextInput,
  UI,
} from "./candidate-ui";

interface EvaluationPreview {
  evaluationId: string;
  candidateName: string;
  candidateSubtitle?: string;
  contextLabel: string;
  result: LevelAssessmentResult;
}

interface PromoteResult {
  candidate: { id: string };
}

/** Accept a bare id or a `…/e/{id}` share URL; pull out the last path segment. */
function extractEvaluationId(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  const noQuery = trimmed.split(/[?#]/)[0];
  const segments = noQuery.split("/").filter(Boolean);
  return segments.length ? segments[segments.length - 1] : trimmed;
}

export function PromoteModal({
  onClose,
  onPromoted,
}: {
  onClose: () => void;
  onPromoted: (candidateId: string) => void;
}) {
  const [input, setInput] = useState("");
  const [preview, setPreview] = useState<EvaluationPreview | null>(null);
  const [name, setName] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // Debounced preview fetch whenever the pasted id changes.
  useEffect(() => {
    const evaluationId = extractEvaluationId(input);
    setPreview(null);
    setPreviewError(null);
    if (!evaluationId) return;
    let cancelled = false;
    setLoadingPreview(true);
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const res = (await rpcClient.labs.levelAssessmentEvaluationGet({
            evaluationId,
          })) as EvaluationPreview;
          if (!cancelled) setPreview(res);
        } catch (e) {
          if (!cancelled)
            setPreviewError(
              e instanceof Error ? e.message : "Não encontramos essa avaliação. Confira o link.",
            );
        } finally {
          if (!cancelled) setLoadingPreview(false);
        }
      })();
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [input]);

  async function onSubmit() {
    setError(null);
    if (!preview) return setError("Cole o link de uma avaliação válida.");
    setSubmitting(true);
    try {
      const res = (await rpcClient.labs.levelAssessmentCandidatePromote({
        evaluationId: preview.evaluationId,
        name: name.trim() || undefined,
      })) as PromoteResult;
      onPromoted(res.candidate.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao promover a avaliação.");
      setSubmitting(false);
    }
  }

  return (
    <ModalShell onClose={onClose}>
      <OverlayHeader
        title="Promover avaliação avulsa"
        subtitle="Transforme uma avaliação já feita em um candidato monitorado — ela vira o Round 1, com a pontuação preservada."
        onClose={onClose}
      />
      <Field
        label="Link ou id da avaliação"
        hint="Cole o link do relatório (…/e/…) ou o id da avaliação."
      >
        <TextInput value={input} onChange={setInput} placeholder="/labs/level-assessment/e/…" />
      </Field>

      {loadingPreview && (
        <div style={{ ...UI, fontSize: 13, color: C.ink50 }}>Buscando avaliação…</div>
      )}
      {previewError && <ErrorText>{previewError}</ErrorText>}

      {preview && (
        <>
          <div
            style={{
              border: `1px solid ${C.ink10}`,
              borderRadius: 14,
              padding: 16,
              display: "flex",
              alignItems: "center",
              gap: 13,
              background: C.ink5,
              marginBottom: 16,
            }}
          >
            <Avatar name={preview.candidateName} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ ...DM, fontWeight: 600, color: C.ink }}>{preview.candidateName}</div>
              <div style={{ ...UI, fontSize: 13, color: C.ink50 }}>
                {preview.candidateSubtitle ?? preview.contextLabel}
              </div>
              <div style={{ marginTop: 6 }}>
                <ConfidenceChip value={preview.result.overallConfidence ?? null} />
              </div>
            </div>
            <LevelPill level={preview.result.overallLevel ?? null} red />
          </div>
          <Field label="Nome (opcional — sobrescreve o da avaliação)">
            <TextInput value={name} onChange={setName} placeholder={preview.candidateName} />
          </Field>
        </>
      )}

      {error && <ErrorText>{error}</ErrorText>}
      <OverlayActions>
        <GhostButton onClick={onClose} disabled={submitting}>
          Cancelar
        </GhostButton>
        <PrimaryButton onClick={onSubmit} disabled={submitting || !preview}>
          <ArrowUpFromLine size={16} /> {submitting ? "Promovendo…" : "Promover para monitorado"}
        </PrimaryButton>
      </OverlayActions>
    </ModalShell>
  );
}
